import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Container } from 'reactstrap';

const DashboardPreview: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', image: '/images/dashboard-hero.png' },
    { id: 'products', label: 'Products', image: '/images/dashboard-products.png' },
    { id: 'orders', label: 'Orders', image: '/images/dashboard-orders.png' },
    { id: 'analytics', label: 'Analytics', image: '/images/dashboard-revenue.png' },
  ];

  return (
    <section className="dashboard-preview-section" id="dashboard">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="dashboard-preview-header"
        >
          <span className="section-badge">
            See It In Action
          </span>
          <h2 className="section-title">
            Powerful Dashboard,{' '}
            <span className="highlight">Simple Interface</span>
          </h2>
          <p className="section-subtitle">
            Monitor your sales, profits, stock levels, and order status from a single dashboard. Real-time updates powered by SSE technology — no page refresh needed.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="dashboard-tabs"
        >
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              className={`dashboard-tab ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>

        {/* Screenshot Display */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="dashboard-screenshot-container"
        >
          <div className="screenshot-wrapper">
            <img
              src={tabs[activeTab].image}
              alt={`CodeRom ${tabs[activeTab].label}`}
              className="dashboard-screenshot-img"
              loading="lazy"
            />
          </div>
        </motion.div>
      </Container>

      <style>{`
        .dashboard-preview-section {
          padding: 80px 0 100px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
        }

        .dashboard-preview-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .dashboard-preview-header .section-badge {
          display: inline-block;
          padding: 8px 16px;
          background: linear-gradient(135deg, #dbeafe, #e0e7ff);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          color: #4f46e5;
          margin-bottom: 16px;
        }

        .dashboard-preview-header .section-title {
          font-size: 36px;
          font-weight: 800;
          color: #1f2937;
          margin-bottom: 12px;
        }

        .dashboard-preview-header .section-title .highlight {
          background: linear-gradient(135deg, #f97316, #fb923c);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .dashboard-preview-header .section-subtitle {
          font-size: 18px;
          color: #6b7280;
          max-width: 500px;
          margin: 0 auto;
        }

        .dashboard-tabs {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-bottom: 32px;
          flex-wrap: wrap;
        }

        .dashboard-tab {
          padding: 10px 24px;
          border: none;
          background: #f3f4f6;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .dashboard-tab:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .dashboard-tab.active {
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
        }

        .dashboard-screenshot-container {
          max-width: 1100px;
          margin: 0 auto;
        }

        .screenshot-wrapper {
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15), 0 10px 30px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          background: white;
        }

        .dashboard-screenshot-img {
          width: 100%;
          height: auto;
          display: block;
        }

        @media (max-width: 768px) {
          .dashboard-preview-section {
            padding: 60px 0 80px;
          }

          .dashboard-preview-header .section-title {
            font-size: 28px;
          }

          .dashboard-preview-header .section-subtitle {
            font-size: 16px;
          }

          .dashboard-tab {
            padding: 8px 16px;
            font-size: 13px;
          }
        }
      `}</style>
    </section>
  );
};

export default DashboardPreview;
